import { StepperProvider, useStepperContext } from "app/contexts/stepper";
import { RoleGuard } from "app/guards";
import {
  InscripcionProvider,
  useInscripcionContext,
} from "./inscripcion-context";
import {
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ActividadesEconomicas,
  AfiliacionForm,
  ArchivosForm,
  Celulares,
  CorreosElectronicos,
  Encargados,
  Establecimientos,
  Exportaciones,
  Importaciones,
  InformacionGeneralForm,
  PaginasWeb,
  Telefonos,
} from "./steps";
import { useNavigate } from "react-router-dom";
import { MdArrowBack, MdRefresh } from "react-icons/md";

export function InscripcionForm() {
  return (
    <RoleGuard roles={["empresa:inscripcion-form"]}>
      <InscripcionProvider>
        <StepsProvider>
          <StepperForm />
        </StepsProvider>
      </InscripcionProvider>
    </RoleGuard>
  );
}

function StepsProvider({ children }) {
  const steps = [
    {
      label: "Información General",
      description: "Información general de la empresa.",
      component: <InformacionGeneralForm />,
      required: true,
    },
    {
      label: "Establecimientos",
      description: "Establecimientos de la empresa.",
      component: <Establecimientos />,
      required: true,
    },
    {
      label: "Números de teléfono",
      description: "Números de teléfono de la empresa.",
      component: <Telefonos />,
      required: false,
    },
    {
      label: "Números de celular",
      description: "Números de celular de la empresa.",
      component: <Celulares />,
      required: true,
    },
    {
      label: "Direcciones de correo electrónico",
      description: "Direcciones de correo electrónico de la empresa.",
      component: <CorreosElectronicos />,
      required: true,
    },
    {
      label: "Direcciones de página web",
      description: "Direcciones de página web de la empresa.",
      component: <PaginasWeb />,
      required: false,
    },
    {
      label: "Encargados",
      description: "Encargados de la empresa.",
      component: <Encargados />,
      required: true,
    },
    {
      label: "Actividades Económicas",
      description: "Actividades Económicas de la empresa.",
      component: <ActividadesEconomicas />,
      required: true,
    },
    {
      label: "Importaciones",
      description: "Importaciones de la empresa.",
      component: <Importaciones />,
      required: false,
    },
    {
      label: "Exportaciones",
      description: "Exportaciones de la empresa.",
      component: <Exportaciones />,
      required: false,
    },
    {
      label: "Afiliación CCIA",
      description:
        "Afiliación de la empresa a la Cámara de Comercio e Industrias de Atlántida",
      component: <AfiliacionForm />,
      required: false,
    },
    {
      label: "Archivos Adjuntos",
      description: "Archivos adjuntos de la empresa.",
      component: <ArchivosForm />,
      required: true,
    },
  ];

  return <StepperProvider steps={steps}>{children}</StepperProvider>;
}

function StepperForm() {
  const navigate = useNavigate();

  const stepper = useStepperContext();
  const inscripcion = useInscripcionContext();

  function reset() {
    const confirmed = window.confirm(
      "Esta acción borrará el progreso de los datos, ¿Desea reiniciar el formulario de inscripción?"
    );

    if (!confirmed) return;

    stepper.reset();
    inscripcion.reset();
  }

  return (
    <Container
      sx={{ padding: 2 }}
      className="animate__animated animate__fadeIn"
    >
      <Paper variant="outlined">
        <Stack spacing={2} padding={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Atrás">
              <IconButton onClick={() => navigate("../..", { replace: true })}>
                <MdArrowBack />
              </IconButton>
            </Tooltip>

            <Stack direction="row" alignItems="center" padding={2}>
              <Typography variant="h6">
                <strong>Formulario de inscripción</strong>
              </Typography>

              <Tooltip title="Reiniciar">
                <IconButton onClick={reset}>
                  <MdRefresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Container>
            <Stepper activeStep={stepper.activeStep} orientation="vertical">
              {stepper.steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel>{step.label}</StepLabel>

                  <StepContent>
                    <Typography variant="overline">
                      {step.description}
                    </Typography>
                    <Box>{step.component}</Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {!!stepper.isFinish && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                padding={4}
              >
                <Typography>Inscripción completada</Typography>

                <Button onClick={reset}>Reiniciar</Button>
              </Stack>
            )}
          </Container>
        </Stack>
      </Paper>
    </Container>
  );
}
